import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  CreditCard, 
  Calendar, 
  AlertCircle, 
  CheckCircle, 
  Clock,
  X,
  Phone,
  Mail,
  Globe,
  DollarSign,
  FileText,
  Search,
  Filter,
  Trash2,
  Printer
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TenantBillingManagement = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('due_date');
  
  // Edit & Delete modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  
  // Bulk Delete
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [editFormData, setEditFormData] = useState({
    payment_method: '',
    next_billing_date: '',
    last_payment_date: '',
    notes: ''
  });

  useEffect(() => {
    fetchTenantBilling();
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/company-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success && data.data) {
        setCompanyInfo({
          company_name: data.data.company_name || 'RadiusLink',
          logo_url: data.data.logo_url,
          logo_invoice_url: data.data.logo_invoice_url,
          address: data.data.company_address,
          phone: data.data.company_phone,
          bank_name: data.data.bank_accounts?.[0]?.bank_name || '',
          bank_account: data.data.bank_accounts?.[0]?.account_number || '',
          bank_holder: data.data.bank_accounts?.[0]?.account_holder || ''
        });
      } else {
        // Fallback to default if API fails
        setCompanyInfo({
          company_name: 'RadiusLink',
          bank_name: 'BCA',
          bank_account: '1234567890',
          bank_holder: 'PT RadiusLink'
        });
      }
    } catch (error) {
      console.error('Error fetching company info:', error);
      // Fallback to default on error
      setCompanyInfo({
        company_name: 'RadiusLink',
        bank_name: 'BCA',
        bank_account: '1234567890',
        bank_holder: 'PT RadiusLink'
      });
    }
  };

  const fetchTenantBilling = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const startTime = Date.now();
      
      // Use V2 MySQL endpoint for faster response
      const response = await fetch(`${API_URL}/api/v2/superadmin/tenant-billing`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      
      console.log(`[TenantBilling] API response time: ${Date.now() - startTime}ms`);
      
      if (data.success) {
        setTenants(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching tenant billing:', error);
      toast.error('Gagal memuat data billing');
    } finally {
      setLoading(false);
    }
  };

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getPaymentStatusBadge = (daysUntilDue, status) => {
    if (status === 'trial') {
      return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40">Trial</Badge>;
    }
    
    if (daysUntilDue === null) {
      return <Badge className="bg-gray-100 text-gray-800 dark:bg-slate-800 dark:text-slate-200">N/A</Badge>;
    }
    
    if (daysUntilDue < 0) {
      return <Badge className="bg-red-100 text-red-800 dark:bg-red-900/40">Overdue {Math.abs(daysUntilDue)}d</Badge>;
    } else if (daysUntilDue <= 7) {
      return <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-900/40">Due in {daysUntilDue}d</Badge>;
    } else if (daysUntilDue <= 30) {
      return <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/40">Due in {daysUntilDue}d</Badge>;
    } else {
      return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/40">Active</Badge>;
    }
  };

  const filteredAndSortedTenants = tenants
    .filter(tenant => {
      const matchesSearch = !searchTerm || 
        tenant.tenant_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tenant.tenant_id?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesFilter = filterStatus === 'all' || 
        (filterStatus === 'overdue' && getDaysUntilDue(tenant.next_billing_date) < 0) ||
        (filterStatus === 'due_soon' && getDaysUntilDue(tenant.next_billing_date) >= 0 && getDaysUntilDue(tenant.next_billing_date) <= 7) ||
        (filterStatus === 'active' && getDaysUntilDue(tenant.next_billing_date) > 7) ||
        (filterStatus === 'trial' && tenant.subscription_status === 'trial');
      
      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      if (sortBy === 'due_date') {
        return new Date(a.next_billing_date) - new Date(b.next_billing_date);
      } else if (sortBy === 'tenant_name') {
        return (a.tenant_name || '').localeCompare(b.tenant_name || '');
      } else if (sortBy === 'amount') {
        return (b.subscription_plan?.monthly_price || 0) - (a.subscription_plan?.monthly_price || 0);
      }
      return 0;
    });

  const stats = {
    overdue: tenants.filter(t => getDaysUntilDue(t.next_billing_date) < 0).length,
    due_soon: tenants.filter(t => {
      const days = getDaysUntilDue(t.next_billing_date);
      return days >= 0 && days <= 7;
    }).length,
    active: tenants.filter(t => getDaysUntilDue(t.next_billing_date) > 7).length,
    total_mrr: tenants.reduce((sum, t) => sum + (t.subscription_plan?.monthly_price || 0), 0)
  };

  const handleViewInvoice = (tenant) => {
    setSelectedTenant(tenant);
    setShowInvoiceModal(true);
  };

  const handlePrintInvoice = () => {
    const printContent = document.querySelector('.print-content');
    
    if (printContent) {
      const clonedContent = printContent.cloneNode(true);
      clonedContent.classList.add('cloned-print-content');
      document.body.appendChild(clonedContent);
      window.print();
      setTimeout(() => {
        document.body.removeChild(clonedContent);
        setShowInvoiceModal(false);
      }, 1000);
    }
  };

  const handleEditBilling = (tenant) => {
    setSelectedTenant(tenant);
    setEditFormData({
      payment_method: tenant.payment_method || 'Manual Transfer',
      next_billing_date: tenant.next_billing_date?.split('T')[0] || '',
      last_payment_date: tenant.last_payment_date?.split('T')[0] || '',
      notes: tenant.billing_notes || ''
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      // Use V2 MySQL endpoint
      const response = await fetch(`${API_URL}/api/v2/superadmin/tenant-billing/${selectedTenant.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editFormData)
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success('Billing info updated successfully');
        setShowEditModal(false);
        fetchTenantBilling();
      } else {
        toast.error(data.detail || 'Failed to update');
      }
    } catch (error) {
      console.error('Error updating billing:', error);
      toast.error('Error updating billing info');
    }
  };

  const handleDeleteBilling = (tenant) => {
    setSelectedTenant(tenant);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      // Use V2 MySQL endpoint for tenant operations
      const response = await fetch(`${API_URL}/api/v2/tenants/${selectedTenant.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success('Tenant deleted');
        setShowDeleteModal(false);
        fetchTenantBilling();
      } else {
        toast.error(data.detail || 'Failed to delete');
      }
    } catch (error) {
      console.error('Error deleting tenant:', error);
      toast.error('Error deleting tenant');
    }
  };

  // Bulk Delete Functions
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedTenants(filteredAndSortedTenants.map(t => t.id));
    } else {
      setSelectedTenants([]);
    }
  };

  const handleSelectTenant = (tenantId) => {
    setSelectedTenants(prev => {
      if (prev.includes(tenantId)) {
        return prev.filter(id => id !== tenantId);
      } else {
        return [...prev, tenantId];
      }
    });
  };

  const handleBulkDelete = async () => {
    if (selectedTenants.length === 0) return;
    
    setShowBulkDeleteConfirm(false);
    
    try {
      const token = localStorage.getItem('token');
      
      toast.info(`Deleting ${selectedTenants.length} tenant(s)...`);

      // Use bulk delete endpoint (1 request instead of loop)
      const response = await fetch(`${API_URL}/api/tenants/bulk-delete`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tenant_ids: selectedTenants })
      });

      const data = await response.json();
      
      if (data.success) {
        const { deleted_count, failed_count, failed_ids } = data;
        
        if (deleted_count > 0) {
          toast.success(`✅ ${deleted_count} tenant(s) deleted successfully`);
        }
        if (failed_count > 0) {
          toast.error(`❌ Failed to delete ${failed_count} tenant(s)`);
          console.error('Failed tenant IDs:', failed_ids);
        }
      }

      setSelectedTenants([]);
      fetchTenantBilling();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error during bulk delete');
    }
  };


  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 15mm;
          }
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          /* Hide everything except cloned print content */
          body > *:not(.cloned-print-content) {
            display: none !important;
          }
          
          .no-print {
            display: none !important;
          }
          
          /* Show cloned print content */
          body > .cloned-print-content,
          body > .cloned-print-content * {
            visibility: visible !important;
          }
          
          body > .cloned-print-content {
            display: block !important;
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
          }
        }
      `}</style>
      
      <div className="p-6 bg-gray-50 min-h-screen print:hidden dark:bg-slate-900">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 dark:text-slate-100">Tenant Billing & Subscriptions</h1>
          <p className="text-gray-600 dark:text-slate-300">Monitor pembayaran, jatuh tempo, dan status subscription tenant</p>
        </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
              <span className="text-sm text-gray-500 dark:text-slate-400">Overdue</span>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">{stats.overdue}</p>
            <p className="text-sm text-gray-600 dark:text-slate-300">Tenants</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-8 h-8 text-orange-600 dark:text-orange-400" />
              <span className="text-sm text-gray-500 dark:text-slate-400">Due Soon</span>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">{stats.due_soon}</p>
            <p className="text-sm text-gray-600 dark:text-slate-300">Next 7 days</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
              <span className="text-sm text-gray-500 dark:text-slate-400">Active</span>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">{stats.active}</p>
            <p className="text-sm text-gray-600 dark:text-slate-300">Tenants</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              <span className="text-sm text-gray-500 dark:text-slate-400">MRR</span>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">
              Rp {(stats.total_mrr / 1000).toFixed(0)}K
            </p>
            <p className="text-sm text-gray-600 dark:text-slate-300">Monthly Revenue</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6 dark:bg-slate-800 dark:border-slate-700">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Cari tenant..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md dark:border-slate-600"
            />
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md dark:border-slate-600"
          >
            <option value="all">All Status</option>
            <option value="overdue">Overdue</option>
            <option value="due_soon">Due Soon (7 days)</option>
            <option value="active">Active</option>
            <option value="trial">Trial</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md dark:border-slate-600"
          >
            <option value="due_date">Sort by Due Date</option>
            <option value="tenant_name">Sort by Name</option>
            <option value="amount">Sort by Amount</option>
          </select>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedTenants.length > 0 && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between dark:bg-blue-900/30">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={selectedTenants.length === filteredAndSortedTenants.length}
              onChange={handleSelectAll}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded dark:text-blue-400 dark:border-slate-600"
            />
            <span className="font-medium text-blue-900">
              {selectedTenants.length} tenant(s) selected
            </span>
          </div>
          <button
            onClick={() => setShowBulkDeleteConfirm(true)}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Delete Selected
          </button>
        </div>
      )}

      {/* Billing Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 dark:bg-slate-800 dark:border-slate-700">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200 dark:bg-slate-900 dark:border-slate-700">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedTenants.length === filteredAndSortedTenants.length && filteredAndSortedTenants.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded dark:text-blue-400 dark:border-slate-600"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-slate-400">Tenant</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-slate-400">Plan</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-slate-400">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-slate-400">Jatuh Tempo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-slate-400">Pembayaran</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-slate-400">Amount</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase dark:text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500 dark:text-slate-400">
                    Loading...
                  </td>
                </tr>
              ) : filteredAndSortedTenants.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500 dark:text-slate-400">
                    No tenants found
                  </td>
                </tr>
              ) : (
                filteredAndSortedTenants.map((tenant) => {
                  const daysUntilDue = getDaysUntilDue(tenant.next_billing_date);
                  
                  return (
                    <tr key={tenant.id} className={`hover:bg-gray-50 ${selectedTenants.includes(tenant.id) ? 'bg-blue-50' : ''}`}>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedTenants.includes(tenant.id)}
                          onChange={() => handleSelectTenant(tenant.id)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded dark:text-blue-400 dark:border-slate-600"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-slate-100">{tenant.tenant_name}</p>
                          <p className="text-xs text-gray-500 dark:text-slate-400">{tenant.tenant_id}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs font-medium text-gray-900 dark:text-slate-100">
                          {tenant.subscription_plan?.plan_name || 'N/A'}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        {getPaymentStatusBadge(daysUntilDue, tenant.subscription_status)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 text-gray-400" />
                          <span className="text-xs text-gray-900 dark:text-slate-100">
                            {tenant.next_billing_date 
                              ? new Date(tenant.next_billing_date).toLocaleDateString('id-ID')
                              : 'N/A'}
                          </span>
                        </div>
                        {daysUntilDue !== null && (
                          <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                            {daysUntilDue < 0 
                              ? `Terlambat ${Math.abs(daysUntilDue)} hari`
                              : `${daysUntilDue} hari lagi`}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <CreditCard className="w-3 h-3 text-gray-400" />
                          <span className="text-xs text-gray-900 dark:text-slate-100">
                            {tenant.payment_method || 'Manual'}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                          {tenant.last_payment_date 
                            ? `Last: ${new Date(tenant.last_payment_date).toLocaleDateString('id-ID')}`
                            : 'No payment yet'}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs font-semibold text-gray-900 dark:text-slate-100">
                          Rp {(tenant.subscription_plan?.monthly_price || 0).toLocaleString('id-ID')}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-400">/bulan</p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => handleViewInvoice(tenant)}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded dark:text-blue-400 dark:hover:bg-blue-900/30"
                            title="View Invoice"
                          >
                            <FileText className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEditBilling(tenant)}
                            className="p-1 text-green-600 hover:bg-green-50 rounded dark:text-green-400 dark:hover:bg-green-900/30"
                            title="Edit Billing Info"
                          >
                            <DollarSign className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteBilling(tenant)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded dark:text-red-400 dark:hover:bg-red-900/30"
                            title="Delete Billing Record"
                          >
                            <AlertCircle className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && selectedTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 dark:bg-slate-800">
            <h3 className="text-lg font-bold mb-4">Edit Billing Info - {selectedTenant.tenant_name}</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">
                  Payment Method
                </label>
                <select
                  value={editFormData.payment_method}
                  onChange={(e) => setEditFormData({...editFormData, payment_method: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md dark:border-slate-600"
                >
                  <option value="Manual Transfer">Manual Transfer</option>
                  <option value="Auto Debit">Auto Debit</option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="Virtual Account">Virtual Account</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">
                  Next Billing Date
                </label>
                <input
                  type="date"
                  value={editFormData.next_billing_date}
                  onChange={(e) => setEditFormData({...editFormData, next_billing_date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md dark:border-slate-600"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">
                  Last Payment Date
                </label>
                <input
                  type="date"
                  value={editFormData.last_payment_date}
                  onChange={(e) => setEditFormData({...editFormData, last_payment_date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md dark:border-slate-600"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">
                  Notes
                </label>
                <textarea
                  value={editFormData.notes}
                  onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md dark:border-slate-600"
                  rows="3"
                  placeholder="Payment notes or remarks..."
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button onClick={handleSaveEdit} className="flex-1">
                Save Changes
              </Button>
              <Button 
                onClick={() => setShowEditModal(false)} 
                variant="outline" 
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 dark:bg-slate-800">
            <h3 className="text-lg font-bold mb-4">Delete Billing Record</h3>
            <p className="text-gray-600 mb-6 dark:text-slate-300">
              Are you sure you want to delete billing record for <strong>{selectedTenant.tenant_name}</strong>?
              This will reset billing information but won't delete the tenant.
            </p>
            <div className="flex gap-3">
              <Button onClick={confirmDelete} className="flex-1 bg-red-600 hover:bg-red-700">
                Delete
              </Button>
              <Button 
                onClick={() => setShowDeleteModal(false)} 
                variant="outline" 
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Modal */}
      {showInvoiceModal && selectedTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={() => setShowInvoiceModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full my-8 max-h-[90vh] overflow-y-auto dark:bg-slate-800" onClick={(e) => e.stopPropagation()}>
            {/* Invoice Content - This will be printed */}
            <div className="px-8 py-6 print-content">
              {/* A4 FORMAT - Modern Design */}
              {/* Header with Logo and Invoice Title */}
              <div className="flex justify-between items-start mb-4">
                {/* Company Info - Left */}
                <div>
                  {/* Logo - Bigger, no company name text */}
                  {(companyInfo?.logo_invoice_url || companyInfo?.logo_url) ? (
                    <div className="mb-3">
                      <img 
                        src={companyInfo?.logo_invoice_url || companyInfo?.logo_url} 
                        alt={companyInfo.company_name || 'RadiusLink'} 
                        className="h-16 max-w-[250px] object-contain"
                        onError={(e) => {
                          console.error('Error loading logo:', e);
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  ) : (
                    <h1 className="text-2xl font-bold text-indigo-600 mb-2 dark:text-indigo-400">
                      {companyInfo?.company_name || 'RadiusLink'}
                    </h1>
                  )}
                  <div className="text-sm text-gray-600 space-y-1 dark:text-slate-300">
                    {companyInfo?.address && <p>{companyInfo.address}</p>}
                    {companyInfo?.city && companyInfo?.province && (
                      <p>{companyInfo.city}, {companyInfo.province} {companyInfo.postal_code}</p>
                    )}
                    {companyInfo?.phone && (
                      <p className="flex items-center gap-2">
                        <Phone className="w-3 h-3" />
                        {companyInfo.phone}
                      </p>
                    )}
                    {companyInfo?.email && (
                      <p className="flex items-center gap-2">
                        <Mail className="w-3 h-3" />
                        {companyInfo.email}
                      </p>
                    )}
                    {companyInfo?.website && (
                      <p className="flex items-center gap-2">
                        <Globe className="w-3 h-3" />
                        {companyInfo.website}
                      </p>
                    )}
                  </div>
                </div>

                {/* Invoice Details - Right */}
                <div className="text-right">
                  <div className="inline-block bg-indigo-50 px-4 py-2 rounded-lg mb-4 dark:bg-indigo-900/30">
                    <h2 className="text-2xl font-bold text-indigo-700 dark:text-indigo-300">INVOICE</h2>
                  </div>
                  <div className="text-sm space-y-2">
                    <div>
                      <span className="text-gray-500 dark:text-slate-400">Invoice #:</span>
                      <span className="font-semibold ml-2">INV-{new Date().getFullYear()}-{String(new Date().getMonth() + 1).padStart(2, '0')}-{selectedTenant.tenant_id?.slice(-6).toUpperCase()}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-slate-400">Date:</span>
                      <span className="font-semibold ml-2">{new Date().toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-slate-400">Due Date:</span>
                      <span className="font-semibold ml-2">{new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t-2 border-indigo-100 mb-5"></div>

              {/* Bill To Section */}
              <div className="grid grid-cols-2 gap-6 mb-5">
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2 dark:text-slate-400">Bill To:</h3>
                  <div className="bg-gray-50 p-3 rounded-lg dark:bg-slate-900">
                    <p className="font-bold text-base text-gray-900 mb-1 dark:text-slate-100">{selectedTenant.tenant_name}</p>
                    <div className="text-xs text-gray-600 space-y-0.5 dark:text-slate-300">
                      {selectedTenant.contact_email && <p>{selectedTenant.contact_email}</p>}
                      {selectedTenant.contact_phone && <p>{selectedTenant.contact_phone}</p>}
                      {selectedTenant.address && <p>{selectedTenant.address}</p>}
                      {selectedTenant.city && <p>{selectedTenant.city}</p>}
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2 dark:text-slate-400">Subscription Details:</h3>
                  <div className="bg-indigo-50 p-3 rounded-lg space-y-1.5 text-xs dark:bg-indigo-900/30">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-slate-300">Plan:</span>
                      <span className="font-semibold text-indigo-700 dark:text-indigo-300">{selectedTenant.subscription_plan?.plan_name || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-slate-300">Status:</span>
                      <span className="font-semibold">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          selectedTenant.subscription_status === 'active' ? 'bg-green-100 text-green-700' :
                          selectedTenant.subscription_status === 'trial' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {selectedTenant.subscription_status?.toUpperCase()}
                        </span>
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-slate-300">Billing Period:</span>
                      <span className="font-semibold">Monthly</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Items Table */}
              <div className="mb-5">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-indigo-600 text-white">
                      <th className="text-left py-2 px-3 font-semibold text-xs">Description</th>
                      <th className="text-center py-2 px-3 font-semibold text-xs">Qty</th>
                      <th className="text-right py-2 px-3 font-semibold text-xs">Unit Price</th>
                      <th className="text-right py-2 px-3 font-semibold text-xs">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                    <tr className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                      <td className="py-2.5 px-3">
                        <div>
                          <p className="font-semibold text-gray-900 text-sm dark:text-slate-100">Subscription Fee</p>
                          <p className="text-xs text-gray-500 dark:text-slate-400">{selectedTenant.subscription_plan?.plan_name || 'Monthly Subscription'}</p>
                        </div>
                      </td>
                      <td className="text-center py-2.5 px-3">1</td>
                      <td className="text-right py-2.5 px-3 text-sm">
                        Rp {(selectedTenant.subscription_plan?.monthly_price || 0).toLocaleString('id-ID')}
                      </td>
                      <td className="text-right py-2.5 px-3 font-semibold text-sm">
                        Rp {(selectedTenant.subscription_plan?.monthly_price || 0).toLocaleString('id-ID')}
                      </td>
                    </tr>
                    {selectedTenant.white_label_enabled && (
                      <tr className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                        <td className="py-2.5 px-3">
                          <div>
                            <p className="font-semibold text-gray-900 text-sm dark:text-slate-100">White Label Service</p>
                            <p className="text-xs text-gray-500 dark:text-slate-400">Custom branding & domain</p>
                          </div>
                        </td>
                        <td className="text-center py-2.5 px-3">1</td>
                        <td className="text-right py-2.5 px-3 text-sm">
                          Rp {(selectedTenant.white_label_fee || 100000).toLocaleString('id-ID')}
                        </td>
                        <td className="text-right py-2.5 px-3 font-semibold text-sm">
                          Rp {(selectedTenant.white_label_fee || 100000).toLocaleString('id-ID')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Total Section */}
              <div className="flex justify-end mb-5">
                <div className="w-72">
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm dark:bg-slate-900">
                    <div className="flex justify-between text-gray-600 dark:text-slate-300">
                      <span>Subtotal:</span>
                      <span className="font-semibold">
                        Rp {(
                          (selectedTenant.subscription_plan?.monthly_price || 0) + 
                          (selectedTenant.white_label_enabled ? (selectedTenant.white_label_fee || 100000) : 0)
                        ).toLocaleString('id-ID')}
                      </span>
                    </div>
                    <div className="flex justify-between text-gray-600 dark:text-slate-300">
                      <span>Tax (0%):</span>
                      <span className="font-semibold">Rp 0</span>
                    </div>
                    <div className="border-t-2 border-gray-300 pt-2 dark:border-slate-600">
                      <div className="flex justify-between items-center">
                        <span className="text-base font-bold text-gray-900 dark:text-slate-100">Total Amount:</span>
                        <span className="text-xl font-bold text-indigo-600 dark:text-indigo-400">
                          Rp {(
                            (selectedTenant.subscription_plan?.monthly_price || 0) + 
                            (selectedTenant.white_label_enabled ? (selectedTenant.white_label_fee || 100000) : 0)
                          ).toLocaleString('id-ID')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-blue-50 rounded-lg text-xs dark:bg-blue-900/30">
                <p className="text-blue-800">
                  <strong>Payment Method:</strong> {selectedTenant.payment_method || 'Manual Transfer'}
                </p>
                <p className="text-blue-800 mt-0.5">
                  <strong>Due Date:</strong> {selectedTenant.next_billing_date 
                    ? new Date(selectedTenant.next_billing_date).toLocaleDateString('id-ID')
                    : 'N/A'}
                </p>
              </div>

              {/* Footer Note - Computer Generated */}
              <div className="mt-6 pt-4 border-t border-gray-300 dark:border-slate-600">
                <div className="text-center text-sm text-gray-600 italic dark:text-slate-300">
                  <strong>NOTE :</strong> This is computer generated receipt and does not require physical signature.
                </div>
              </div>
            </div>

            {/* Action Buttons - Hidden on Print */}
            <div className="px-6 pb-4 flex gap-3 border-t pt-4 bg-gray-50 no-print dark:bg-slate-900">
              <Button onClick={handlePrintInvoice} className="flex-1">
                <Printer className="w-4 h-4 mr-2" />
                Print Invoice
              </Button>
              <Button 
                onClick={() => setShowInvoiceModal(false)} 
                variant="outline" 
                className="flex-1"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 dark:bg-slate-800">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center dark:bg-red-900/40">
                <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">Delete Multiple Tenants</h3>
                <p className="text-sm text-gray-600 dark:text-slate-300">This action cannot be undone</p>
              </div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 dark:bg-red-900/30">
              <p className="text-sm text-red-800">
                <strong>⚠️ Warning:</strong> You are about to delete <strong>{selectedTenants.length}</strong> tenant(s). 
                All their data, customers, and configurations will be permanently deleted.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowBulkDeleteConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700/50"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Yes, Delete All
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default TenantBillingManagement;
